/**
 * studioTheme.ts — Studio OS V4 semantic theme system.
 *
 * THEME SWAP GUIDE:
 * To change the visual style, edit DARK or LIGHT palette constants below.
 * Components never reference raw color values — only semantic tokens via
 * useStudioTheme(). Adding a new theme means adding a palette constant and
 * a ThemeMode entry.
 *
 * ARCHITECTURE:
 *   ThemeProvider  — reads localStorage + system pref, injects CSS keyframes
 *   useStudioTheme — returns { ST, theme, setTheme } inside any Studio component
 *   motion()       — pure helper, returns value or 'none' for reduced-motion
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

// ── Theme mode ────────────────────────────────────────────────────────────────

export type ThemeMode = 'dark' | 'light';

// ── Token shape (semantic — same keys in every theme) ─────────────────────────

export interface StudioTokens {
  bg: {
    base:          string;
    surface:       string;
    surfaceHover:  string;
    surfaceActive: string;
    sidebar:       string;
    overlay:       string;
    overlayHover:  string;
  };
  border: {
    subtle:  string;
    default: string;
    strong:  string;
    accent:  string;
    gold:    string;
  };
  text: {
    primary:   string;
    secondary: string;
    muted:     string;
    disabled:  string;
    accent:    string;
    gold:      string;
  };
  accent: {
    purple:       string;
    purpleHover:  string;
    purpleGlow:   string;
    purpleSubtle: string;
    indigo:       string;
    gold:         string;
    goldGlow:     string;
    goldSubtle:   string;
  };
  status: {
    healthy:       string;
    healthyBg:     string;
    healthyBorder: string;
    degraded:       string;
    degradedBg:     string;
    degradedBorder: string;
    down:          string;
    downBg:        string;
    downBorder:    string;
    inSync:        string;
    needsSync:     string;
    outdated:      string;
  };
  workspace: Record<string, { icon: string; bg: string }>;
  workspaceFallback: { icon: string; bg: string };
  gradient: {
    card:    string;
    hero:    string;
    accent:  string;
    sidebar: string;
    gold:    string;
  };
  shadow: {
    card:       string;
    cardHover:  string;
    cardActive: string;
    glow:       string;
    goldGlow:   string;
    sm:         string;
    module:     string;
    inset:      string;
  };
  radius: { xs: number; sm: number; md: number; lg: number; xl: number; xxl: number; full: number };
  space:  { 1: number; 2: number; 3: number; 4: number; 5: number; 6: number; 8: number; 10: number; 12: number };
  font:   { sans: string; heading: string; mono: string };
  motion: { fast: string; default: string; slow: string; lift: string; fade: string; spring: string };
  layout: { sidebarWidth: number; rightPanelWidth: number };
  glass: {
    surface:      string;
    surfaceHover: string;
    border:       string;
    borderHover:  string;
    blur:         string;
  };
  ambient: {
    hero: string;
  };
}

// ── Shared values (identical in every theme) ──────────────────────────────────

const SHARED = {
  // Workspace identity colors — stable brand values, not UI chrome
  workspace: {
    frontend:    { icon: '#61dafb', bg: 'rgba(97,218,251,0.12)'  },
    gameplay:    { icon: '#a855f7', bg: 'rgba(168,85,247,0.12)'  },
    platform:    { icon: '#22c55e', bg: 'rgba(34,197,94,0.12)'   },
    social:      { icon: '#f97316', bg: 'rgba(249,115,22,0.12)'  },
    'studio-os': { icon: '#06b6d4', bg: 'rgba(6,182,212,0.12)'   },
  } as Record<string, { icon: string; bg: string }>,
  workspaceFallback: { icon: 'rgba(255,255,255,0.4)', bg: 'rgba(255,255,255,0.07)' },
  accent: {
    gold:    '#d4920a',
    indigo:  '#6366f1',
  },
  gradient: {
    accent: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
  },
  radius: { xs: 4,    sm: 6,   md: 10,  lg: 14,  xl: 18, xxl: 24, full: 9999 },
  space:  { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 8: 32, 10: 40, 12: 48 },
  font:   { sans: 'Montserrat, sans-serif', heading: 'Nunito, sans-serif', mono: 'monospace' },
  motion: {
    fast:    '0.12s ease',
    default: '0.20s ease',
    slow:    '0.30s ease',
    lift:    'transform 0.20s ease, box-shadow 0.20s ease, border-color 0.20s ease, background 0.20s ease',
    fade:    'opacity 0.20s ease',
    spring:  '0.4s cubic-bezier(0.34,1.56,0.64,1)',
  },
  layout: { sidebarWidth: 220, rightPanelWidth: 300 },
} as const;

// ── Dark palette ──────────────────────────────────────────────────────────────
// Elevation = lighter backgrounds. Shadows are minimal.

const DARK: StudioTokens = {
  bg: {
    base:          '#080a12',
    surface:       '#0e1118',
    surfaceHover:  '#121720',
    surfaceActive: '#161c28',
    sidebar:       '#090b14',
    overlay:       'rgba(255,255,255,0.025)',
    overlayHover:  'rgba(255,255,255,0.04)',
  },
  border: {
    subtle:  'rgba(255,255,255,0.04)',
    default: 'rgba(255,255,255,0.08)',
    strong:  'rgba(255,255,255,0.14)',
    accent:  'rgba(124,58,237,0.4)',
    gold:    'rgba(212,146,10,0.35)',
  },
  text: {
    primary:   'rgba(255,255,255,0.92)',
    secondary: 'rgba(255,255,255,0.6)',
    muted:     'rgba(255,255,255,0.35)',
    disabled:  'rgba(255,255,255,0.18)',
    accent:    '#a78bfa',
    gold:      '#d4920a',
  },
  accent: {
    purple:       '#7c3aed',
    purpleHover:  '#6d28d9',
    purpleGlow:   'rgba(124,58,237,0.28)',
    purpleSubtle: 'rgba(124,58,237,0.12)',
    indigo:       SHARED.accent.indigo,
    gold:         SHARED.accent.gold,
    goldGlow:     'rgba(212,146,10,0.22)',
    goldSubtle:   'rgba(212,146,10,0.10)',
  },
  status: {
    healthy:        '#22c55e',
    healthyBg:      'rgba(34,197,94,0.10)',
    healthyBorder:  'rgba(34,197,94,0.28)',
    degraded:        '#f59e0b',
    degradedBg:      'rgba(245,158,11,0.10)',
    degradedBorder:  'rgba(245,158,11,0.28)',
    down:           '#ef4444',
    downBg:         'rgba(239,68,68,0.10)',
    downBorder:     'rgba(239,68,68,0.28)',
    inSync:         '#22c55e',
    needsSync:      '#f59e0b',
    outdated:       '#ef4444',
  },
  workspace:         SHARED.workspace,
  workspaceFallback: SHARED.workspaceFallback,
  gradient: {
    card:    'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
    hero:    'linear-gradient(135deg, rgba(124,58,237,0.18) 0%, rgba(99,102,241,0.07) 60%, transparent 100%)',
    accent:  SHARED.gradient.accent,
    sidebar: 'linear-gradient(180deg, #090b14 0%, #080a12 100%)',
    gold:    'linear-gradient(135deg, rgba(212,146,10,0.20) 0%, rgba(212,146,10,0.05) 100%)',
  },
  shadow: {
    card:       '0 1px 3px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)',
    cardHover:  '0 8px 32px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.09), 0 0 28px rgba(124,58,237,0.14)',
    cardActive: '0 4px 16px rgba(0,0,0,0.5), 0 0 0 1px rgba(124,58,237,0.4)',
    glow:       '0 0 32px rgba(124,58,237,0.3)',
    goldGlow:   '0 0 24px rgba(212,146,10,0.25)',
    sm:         '0 1px 2px rgba(0,0,0,0.4)',
    module:     '0 4px 24px rgba(0,0,0,0.6), 0 1px 4px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.055)',
    inset:      'inset 0 1px 0 rgba(255,255,255,0.09)',
  },
  radius: SHARED.radius,
  space:  SHARED.space,
  font:   SHARED.font,
  motion: SHARED.motion,
  layout: SHARED.layout,
  glass: {
    surface:      'rgba(10,13,20,0.80)',
    surfaceHover: 'rgba(14,18,28,0.92)',
    border:       'rgba(255,255,255,0.065)',
    borderHover:  'rgba(255,255,255,0.140)',
    blur:         '20px',
  },
  ambient: {
    hero: 'radial-gradient(ellipse 900px 600px at 20% 35%, rgba(124,58,237,0.09) 0%, transparent 65%), radial-gradient(ellipse 500px 400px at 80% 70%, rgba(99,102,241,0.05) 0%, transparent 60%)',
  },
};

// ── Light palette ─────────────────────────────────────────────────────────────
// Elevation = real shadows. Cards are white; canvas is warm grey.

const LIGHT: StudioTokens = {
  bg: {
    base:          '#eceff8',
    surface:       '#ffffff',
    surfaceHover:  '#f5f7fc',
    surfaceActive: 'rgba(124,58,237,0.06)',
    sidebar:       '#f2f4fb',
    overlay:       'rgba(0,0,0,0.03)',
    overlayHover:  'rgba(0,0,0,0.055)',
  },
  border: {
    subtle:  'rgba(0,0,0,0.07)',
    default: 'rgba(0,0,0,0.11)',
    strong:  'rgba(0,0,0,0.17)',
    accent:  'rgba(124,58,237,0.30)',
    gold:    'rgba(146,98,10,0.35)',
  },
  text: {
    primary:   'rgba(0,0,0,0.88)',
    secondary: 'rgba(0,0,0,0.60)',
    muted:     'rgba(0,0,0,0.40)',
    disabled:  'rgba(0,0,0,0.24)',
    accent:    '#6d28d9',
    gold:      '#92620a',
  },
  accent: {
    purple:       '#7c3aed',
    purpleHover:  '#6d28d9',
    purpleGlow:   'rgba(124,58,237,0.18)',
    purpleSubtle: 'rgba(124,58,237,0.08)',
    indigo:       SHARED.accent.indigo,
    gold:         SHARED.accent.gold,
    goldGlow:     'rgba(212,146,10,0.16)',
    goldSubtle:   'rgba(212,146,10,0.10)',
  },
  status: {
    healthy:        '#16a34a',
    healthyBg:      'rgba(22,163,74,0.10)',
    healthyBorder:  'rgba(22,163,74,0.28)',
    degraded:        '#d97706',
    degradedBg:      'rgba(217,119,6,0.10)',
    degradedBorder:  'rgba(217,119,6,0.28)',
    down:           '#dc2626',
    downBg:         'rgba(220,38,38,0.10)',
    downBorder:     'rgba(220,38,38,0.28)',
    inSync:         '#16a34a',
    needsSync:      '#d97706',
    outdated:       '#dc2626',
  },
  workspace:         SHARED.workspace,
  workspaceFallback: { icon: 'rgba(0,0,0,0.35)', bg: 'rgba(0,0,0,0.06)' },
  gradient: {
    card:    'linear-gradient(145deg, rgba(0,0,0,0.015) 0%, rgba(0,0,0,0.005) 100%)',
    hero:    'linear-gradient(135deg, rgba(124,58,237,0.09) 0%, rgba(99,102,241,0.04) 60%, transparent 100%)',
    accent:  SHARED.gradient.accent,
    sidebar: 'linear-gradient(180deg, #f4f7fe 0%, #eceff8 100%)',
    gold:    'linear-gradient(135deg, rgba(212,146,10,0.12) 0%, rgba(212,146,10,0.04) 100%)',
  },
  shadow: {
    card:       '0 2px 8px rgba(0,0,0,0.09), 0 1px 2px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.07)',
    cardHover:  '0 8px 28px rgba(0,0,0,0.13), 0 2px 6px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.09), 0 0 20px rgba(124,58,237,0.07)',
    cardActive: '0 4px 12px rgba(0,0,0,0.10), 0 0 0 1px rgba(124,58,237,0.32)',
    glow:       '0 0 24px rgba(124,58,237,0.20)',
    goldGlow:   '0 0 18px rgba(212,146,10,0.18)',
    sm:         '0 1px 2px rgba(0,0,0,0.08)',
    module:     '0 4px 24px rgba(0,0,0,0.09), 0 1px 4px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.07)',
    inset:      'inset 0 1px 0 rgba(255,255,255,0.80)',
  },
  radius: SHARED.radius,
  space:  SHARED.space,
  font:   SHARED.font,
  motion: SHARED.motion,
  layout: SHARED.layout,
  glass: {
    surface:      'rgba(255,255,255,0.82)',
    surfaceHover: 'rgba(255,255,255,0.96)',
    border:       'rgba(0,0,0,0.07)',
    borderHover:  'rgba(0,0,0,0.12)',
    blur:         '12px',
  },
  ambient: {
    hero: 'radial-gradient(ellipse 900px 600px at 20% 35%, rgba(124,58,237,0.05) 0%, transparent 65%)',
  },
};

// ── CSS keyframe injection ────────────────────────────────────────────────────

function injectKeyframes(): void {
  const id = 'studio-os-keyframes-v6';
  if (document.getElementById(id)) return;
  const el = document.createElement('style');
  el.id = id;
  el.textContent = `
    /* ── Status dot animations ─────────────────────────────── */
    @keyframes studio-breathe {
      0%, 100% { opacity: 1;    transform: scale(1);    }
      50%       { opacity: 0.52; transform: scale(0.80); }
    }
    @keyframes studio-wave-ring {
      0%   { transform: scale(1);   opacity: 0.55; }
      100% { transform: scale(2.8); opacity: 0;    }
    }
    @keyframes studio-warn {
      0%, 70%, 100% { opacity: 1;    }
      35%            { opacity: 0.18; }
    }
    @keyframes studio-spin {
      from { transform: rotate(0deg);   }
      to   { transform: rotate(360deg); }
    }
    @keyframes studio-shimmer {
      0%, 100% { opacity: 0.32; }
      50%       { opacity: 0.88; }
    }

    /* ── Sidebar floating pill ─────────────────────────────── */
    @keyframes studio-pill-appear {
      from { opacity: 0; transform: scaleX(0.85); }
      to   { opacity: 1; transform: scaleX(1);    }
    }

    /* ── Queue row entrance ────────────────────────────────── */
    @keyframes studio-row-in {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0);   }
    }

    /* ── Gradient border shimmer ───────────────────────────── */
    @keyframes studio-border-shimmer {
      0%   { background-position: 0%   50%; }
      50%  { background-position: 100% 50%; }
      100% { background-position: 0%   50%; }
    }

    /* ── Ambient background drift ──────────────────────────── */
    @keyframes studio-ambient-drift {
      0%   { transform: scale(1)    translate(0%,   0%);   opacity: 0.9; }
      25%  { transform: scale(1.12) translate(4%,  -3%);   opacity: 1;   }
      50%  { transform: scale(1.06) translate(-2%,  5%);   opacity: 0.85;}
      75%  { transform: scale(1.10) translate(3%,   2%);   opacity: 1;   }
      100% { transform: scale(1)    translate(0%,   0%);   opacity: 0.9; }
    }
    @keyframes studio-ambient-drift-b {
      0%   { transform: scale(1)    translate(0%,   0%);   }
      33%  { transform: scale(1.08) translate(-5%,  4%);   }
      66%  { transform: scale(0.94) translate(4%,  -3%);   }
      100% { transform: scale(1)    translate(0%,   0%);   }
    }

    /* ── Avatar ring rotation ──────────────────────────────── */
    @keyframes studio-ring-spin {
      from { transform: rotate(0deg);   }
      to   { transform: rotate(360deg); }
    }

    /* ── Classes ───────────────────────────────────────────── */
    .studio-anim-breathe  { animation: studio-breathe 3s ease-in-out infinite; }
    .studio-anim-wave     { animation: studio-wave-ring 1.8s ease-out infinite; }
    .studio-anim-warn     { animation: studio-warn 1.2s ease-in-out infinite; }
    .studio-anim-spin     { animation: studio-spin 1.6s linear infinite; }
    .studio-anim-shimmer  { animation: studio-shimmer 2.8s ease-in-out infinite; }
    .studio-row-in        { animation: studio-row-in 0.32s cubic-bezier(0.2,0,0,1) both; }
    .studio-ambient-a     { animation: studio-ambient-drift   28s ease-in-out infinite; will-change: transform; }
    .studio-ambient-b     { animation: studio-ambient-drift-b 22s ease-in-out infinite; will-change: transform; }
    .studio-avatar-ring   { animation: studio-ring-spin 9s linear infinite; }
    .studio-border-live   {
      background: linear-gradient(90deg, #7c3aed, #06b6d4, #a855f7, #6366f1, #7c3aed);
      background-size: 400% 100%;
      animation: studio-border-shimmer 5s ease infinite;
    }

    /* ── Card entrance ────────────────────────────────────── */
    @keyframes studio-card-in {
      from { opacity: 0; transform: translateY(14px) scale(0.97); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
    .studio-card-in { animation: studio-card-in 0.45s cubic-bezier(0.16,1,0.3,1) both; }

    /* ── Hero atmosphere waves ─────────────────────────────── */
    @keyframes studio-hero-wave-a {
      0%, 100% { transform: scale(1)    translateX(0%);  }
      50%       { transform: scale(1.18) translateX(-5%); }
    }
    @keyframes studio-hero-wave-b {
      0%, 100% { transform: scale(1)    translateX(0%); }
      50%       { transform: scale(1.12) translateX(6%); }
    }
    .studio-hero-wave-a { animation: studio-hero-wave-a 16s ease-in-out infinite; will-change: transform; }
    .studio-hero-wave-b { animation: studio-hero-wave-b 20s ease-in-out infinite; will-change: transform; }

    /* ── Glow pulse ───────────────────────────────────────────── */
    @keyframes studio-glow-pulse {
      0%, 100% { opacity: 0.55; }
      50%       { opacity: 1; }
    }
    .studio-glow-pulse { animation: studio-glow-pulse 2.8s ease-in-out infinite; }

    /* ── Module entrance ──────────────────────────────────────── */
    @keyframes studio-module-in {
      from { opacity: 0; transform: translateY(8px) scale(0.98); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
    .studio-module-in { animation: studio-module-in 0.5s cubic-bezier(0.16,1,0.3,1) both; }

    @media (prefers-reduced-motion: reduce) {
      .studio-anim-breathe,
      .studio-anim-wave,
      .studio-anim-warn,
      .studio-anim-spin,
      .studio-anim-shimmer,
      .studio-row-in,
      .studio-ambient-a,
      .studio-ambient-b,
      .studio-avatar-ring,
      .studio-border-live,
      .studio-card-in,
      .studio-hero-wave-a,
      .studio-hero-wave-b,
      .studio-glow-pulse,
      .studio-module-in { animation: none !important; }
    }
  `;
  document.head.appendChild(el);
}

// ── Context ───────────────────────────────────────────────────────────────────

interface StudioThemeContextValue {
  ST:       StudioTokens;
  theme:    ThemeMode;
  setTheme: (t: ThemeMode) => void;
}

const StudioThemeContext = createContext<StudioThemeContextValue>({
  ST:       DARK,
  theme:    'dark',
  setTheme: () => {},
});

// ── ThemeProvider ─────────────────────────────────────────────────────────────

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    try {
      const saved = localStorage.getItem('studio-theme');
      if (saved === 'dark' || saved === 'light') return saved;
    } catch { /* localStorage unavailable */ }
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
    return 'dark';
  });

  useEffect(() => { injectKeyframes(); }, []);

  useEffect(() => {
    try { localStorage.setItem('studio-theme', theme); } catch { /* ignore */ }
  }, [theme]);

  const setTheme = (t: ThemeMode) => setThemeState(t);
  const ST = theme === 'dark' ? DARK : LIGHT;

  return React.createElement(
    StudioThemeContext.Provider,
    { value: { ST, theme, setTheme } },
    children
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useStudioTheme(): StudioThemeContextValue {
  return useContext(StudioThemeContext);
}

// ── motion helper ─────────────────────────────────────────────────────────────

/** Returns the transition value, or 'none' when user prefers reduced motion. */
export function motion(value: string): string {
  if (typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return 'none';
  }
  return value;
}
